// ENG-6803: conditional-delivery contract for the scheduled-task ONESHOT
// (`claude -p`) path.
//
// Background. A scheduled routine's "When to deliver: Only when it matters"
// setting maps to delivery_policy='conditional'. The in-session kanban route
// enforces that as a structured, suppressed-by-default gate: a run delivers
// only if the agent explicitly asserts delivery on kanban_done
// (suppress_delivery: false). The oneshot path has no kanban card — its
// delivery is the agent's stdout — so there is no boolean to read. The
// equivalent assertion is an explicit DELIVER marker the agent PRINTS, naming
// the concrete trigger that fired this run.
//
// The leak this closes: previously the oneshot path (used by non-plain
// templates on persistent agents, and by the AGT_SCHEDULED_VIA_KANBAN=0
// opt-out) only honoured the `<no-delivery/>` suppress sentinel — it had no
// suppressed-by-default behaviour for conditional tasks. An agent that wrote
// any non-empty narrative (e.g. an hourly "no change, board clean" status)
// delivered every run, because nothing forced it to justify the send. Vera
// DM'd an operator 24x/day this way (ENG-6803).
//
// The contract:
//   - Default: SUPPRESS. A conditional run is silent unless the marker is present.
//   - To deliver, the agent emits `<deliver: REASON>` where REASON names the
//     concrete trigger ("urgent email from the CEO", "CI failing on main").
//   - A marker whose REASON is vacuous ("no change", "all clear", "status
//     update") is rejected — it is suppressed exactly as if absent. This stops
//     the agent from rubber-stamping a delivery without a real reason.
//
// The marker is the oneshot mirror of the kanban path's structured
// suppress_delivery flag; both keep the "machine-checkable, not free-prose"
// property the gate depends on.

/** Literal head of the deliver marker, for teaching prose / tests. */
export const DELIVER_MARKER = '<deliver: ...>';

// The marker must be a STANDALONE line — the teaching contract is "a marker on
// its own line". Anchoring to line start/end keeps a marker quoted mid-prose
// (e.g. the agent explaining "I did NOT emit <deliver: ...>") from accidentally
// opening the gate; for a suppressed-by-default gate a false-OPEN is the worst
// failure, so we err toward only matching a deliberate standalone marker.
// Case-insensitive; agents sometimes wrap the marker in up to three backticks
// (the teaching shows it code-formatted), so swallow those. `[^\n>]` keeps the
// reason on a single line.
const DELIVER_MARKER_REGEX = /(?:^|\n)[ \t]*`{0,3}<deliver:\s*([^\n>]*?)\s*>`{0,3}[ \t]*(?=\n|$)/i;
// Global form used to strip EVERY standalone marker line from the deliverable.
const DELIVER_MARKER_STRIP_REGEX = /(?:^|\n)[ \t]*`{0,3}<deliver:\s*[^\n>]*?\s*>`{0,3}[ \t]*(?=\n|$)/gi;

export interface DeliverAssertion {
  /** True when a `<deliver: ...>` marker with a NON-vacuous reason was found. */
  deliver: boolean;
  /** The reason the agent named, trimmed. Null when no marker was present. */
  reason: string | null;
  /** True when a marker was present but its reason was vacuous (so suppressed
   *  despite the agent asserting). Lets the caller log "asserted-but-vacuous"
   *  distinctly from "no marker at all". */
  vacuous: boolean;
  /** The message to send (marker(s) stripped, blank runs collapsed). Only
   *  meaningful when `deliver` is true; empty string otherwise. */
  deliverable: string;
}

// Words that carry no concrete trigger on their own — a reason built ONLY from
// these (plus stopwords) is vacuous. Deliberately covers the exact phrases
// agents default to ("no change", "all clear", "nothing urgent", "standing
// down", "board clean", "status update", "routine check").
const NOOP_TOKENS = new Set([
  'no', 'none', 'not', 'nothing', 'nil', 'na',
  'change', 'changes', 'changed', 'unchanged',
  'update', 'updates', 'updated',
  'status', 'check', 'checks', 'checked', 'checking',
  'routine', 'regular', 'periodic', 'hourly', 'daily', 'weekly',
  'all', 'clear', 'quiet', 'calm', 'normal', 'usual', 'steady',
  'ok', 'okay', 'fine', 'good', 'green', 'healthy', 'nominal',
  'standing', 'down', 'stand',
  'new', 'news', 'fresh',
  'pending', 'outstanding', 'open', 'work', 'tasks', 'task', 'items', 'item',
  'board', 'clean', 'empty', 'idle',
  'report', 'reporting', 'summary', 'digest',
  'action', 'actions', 'required', 'needed', 'issue', 'issues', 'problem', 'problems',
  'urgent', 'important', 'critical', // bare adjective is not a concrete trigger
  'still', 'same', 'as', 'before', 'last', 'prior', 'previous', 'since', 'run',
  'everything', 'anything', 'something',
]);

// Generic stopwords stripped before the all-noop test so filler can't make a
// vacuous reason read as concrete ("from", "the", etc.).
const STOPWORDS = new Set([
  'a', 'an', 'the', 'is', 'are', 'was', 'were', 'be', 'been',
  'to', 'of', 'in', 'on', 'at', 'for', 'with', 'and', 'or', 'but',
  'this', 'that', 'these', 'those', 'it', 'its', 'so', 'just', 'yet',
  'there', 'here', 'have', 'has', 'had', 'no.', 'i', 'we', 'my', 'our',
]);

/**
 * Is the named reason too vacuous to justify a delivery? True when the reason
 * is empty, or when every meaningful token (after dropping stopwords) is a
 * no-op token. A reason containing ANY concrete word ("email", "CEO", "PR",
 * "deploy", "$4,000", "outage") passes.
 */
export function isVacuousDeliverReason(reason: string | null | undefined): boolean {
  if (reason == null) return true;
  const normalized = reason
    .toLowerCase()
    // Keep $ % # . so "$4,000" / "PR#12" survive as concrete tokens. Hyphens
    // become spaces so hyphenated no-op phrases ("all-clear", "board-clean")
    // tokenize into their no-op parts instead of reading as one concrete word.
    .replace(/[^a-z0-9$%#.\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (normalized.length === 0) return true;

  const tokens = normalized.split(' ').filter((t) => t.length > 0 && !STOPWORDS.has(t));
  if (tokens.length === 0) return true;

  // A purely numeric/symbol token (e.g. "#1234", "$4,000") IS concrete.
  return tokens.every((t) => NOOP_TOKENS.has(t) && !/[$%#0-9]/.test(t));
}

/**
 * Parse the agent's oneshot stdout for an explicit deliver assertion.
 *
 * Returns deliver=false (suppress) when no marker is present, or when the
 * marker's reason is vacuous. Returns deliver=true with the marker(s) stripped
 * from `deliverable` only when a concrete reason was named.
 */
export function parseDeliverAssertion(output: string | null | undefined): DeliverAssertion {
  if (output == null) {
    return { deliver: false, reason: null, vacuous: false, deliverable: '' };
  }
  const match = output.match(DELIVER_MARKER_REGEX);
  if (!match) {
    return { deliver: false, reason: null, vacuous: false, deliverable: '' };
  }
  const reason = (match[1] ?? '').trim();
  if (isVacuousDeliverReason(reason)) {
    return { deliver: false, reason, vacuous: true, deliverable: '' };
  }
  // Strip every marker occurrence and collapse the blank-line runs the removal
  // leaves behind, so we never ship the literal token to the recipient.
  const deliverable = output
    .replace(DELIVER_MARKER_STRIP_REGEX, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { deliver: true, reason, vacuous: false, deliverable };
}
